import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useHttpClient } from "../../hooks/useHttpClient";
import { useUserContext } from "../../stores/userContext";
import { signInWithFirebaseCustomToken } from "../../services/Messages/firestore";
import "./Auth.scss";
import { AuthPageHeader } from "./AuthPageHeader";
import { useLocalization } from "../../stores/localizationContext";

export const VerifyEmailOtp = ({
    title,
    details,
}: {
    title?: string;
    details?: string;
}) => {
    const { t } = useLocalization();
    title = title ?? t("verification_code");
    details = details ?? t("phone_otp_sent_warning_message");
    const location = useLocation();
    const navigate = useNavigate();
    const { login } = useUserContext();
    const api = useHttpClient(false);

    // Get props from navigation state
    const state = location.state as {
        email?: string;
        duration?: number;
        resendingDuration?: number;
    };

    const email = state?.email;
    const initialDuration = state?.duration;
    const initialResendingDuration = state?.resendingDuration;

    // State management (only for things that need re-render)
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [canResend, setCanResend] = useState(false);
    const [isOtpComplete, setIsOtpComplete] = useState(false);

    // Refs for input fields, OTP values, and timers (no re-renders)
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const otpRef = useRef<string[]>(["", "", "", "", "", ""]);
    const durationRef = useRef<number>(initialDuration || 0);
    const resendingDurationRef = useRef<number>(initialResendingDuration || 0);
    const durationTimerDisplayRef = useRef<HTMLParagraphElement>(null);
    const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const resendIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Duration countdown timer - VISIBLE timer shown to user (OTP expiry)
    useEffect(() => {
        if (durationRef.current <= 0) {
            return;
        }

        // Use timestamp-based countdown so background throttling doesn't skew the display
        const endTime = Date.now() + durationRef.current * 1000;

        durationIntervalRef.current = setInterval(() => {
            const remaining = Math.max(0, Math.round((endTime - Date.now()) / 1000));
            durationRef.current = remaining;

            if (durationTimerDisplayRef.current) {
                const minutes = Math.floor(remaining / 60);
                const seconds = remaining % 60;
                durationTimerDisplayRef.current.textContent = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
            }

            if (remaining <= 0 && durationIntervalRef.current) {
                clearInterval(durationIntervalRef.current);
            }
        }, 500);

        return () => {
            if (durationIntervalRef.current) {
                clearInterval(durationIntervalRef.current);
            }
        };
    }, []);

    // Resending duration timer - HIDDEN timer that controls resend button visibility
    useEffect(() => {
        if (resendingDurationRef.current <= 0) {
            setCanResend(true);
            return;
        }

        setCanResend(false);

        // Use timestamp-based countdown so background throttling doesn't skew the result
        const resendEndTime = Date.now() + resendingDurationRef.current * 1000;

        resendIntervalRef.current = setInterval(() => {
            const remaining = Math.max(0, Math.round((resendEndTime - Date.now()) / 1000));
            resendingDurationRef.current = remaining;

            if (remaining <= 0 && resendIntervalRef.current) {
                clearInterval(resendIntervalRef.current);
                setCanResend(true);
            }
        }, 500);

        return () => {
            if (resendIntervalRef.current) {
                clearInterval(resendIntervalRef.current);
            }
        };
    }, []);

    // Format seconds into readable time
    const formatTime = (secs: number) => {
        const minutes = Math.floor(secs / 60);
        const seconds = secs % 60;
        return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
            2,
            "0"
        )}`;
    };

    // Set ref for input field
    const setInputRef = (index: number) => (el: HTMLInputElement | null) => {
        inputRefs.current[index] = el;
    };

    // Handle input change
    const handleChange = (index: number, value: string) => {
        // Only allow numeric values - strip any non-numeric characters
        const numericValue = value.replace(/[^0-9]/g, "");

        // If no valid numeric input, clear the field and return
        if (!numericValue) {
            if (inputRefs.current[index]) {
                inputRefs.current[index]!.value = "";
            }
            return;
        }

        // Take only the last digit if multiple were entered
        const digit = numericValue.slice(-1);

        // Update ref and DOM
        otpRef.current[index] = digit;
        if (inputRefs.current[index]) {
            inputRefs.current[index]!.value = digit;
        }

        // Check if all 6 digits are filled
        const allFilled = otpRef.current.every((d) => d !== "");
        setIsOtpComplete(allFilled);

        // Enable and focus next input
        if (digit && index < 5 && inputRefs.current[index + 1]) {
            // Enable next input FIRST, then focus
            inputRefs.current[index + 1]!.disabled = false;
            inputRefs.current[index + 1]!.focus();
        }
    };

    // Handle keydown events (backspace)
    const handleKeyDown = (
        index: number,
        e: React.KeyboardEvent<HTMLInputElement>
    ) => {
        if (e.key === "Backspace") {
            if (!otpRef.current[index] && index > 0) {
                // Current input is empty, disable it and move to previous input
                if (inputRefs.current[index]) {
                    inputRefs.current[index]!.disabled = true;
                }
                inputRefs.current[index - 1]?.focus();
            } else {
                // Clear current input (update ref and DOM)
                otpRef.current[index] = "";
                if (inputRefs.current[index]) {
                    inputRefs.current[index]!.value = "";
                }

                // Disable all subsequent inputs and clear their values (including current if not first)
                for (let i = index + 1; i < 6; i++) {
                    otpRef.current[i] = "";
                    if (inputRefs.current[i]) {
                        inputRefs.current[i]!.value = "";
                        inputRefs.current[i]!.disabled = true;
                    }
                }

                // Update isOtpComplete state
                setIsOtpComplete(false);

                // Disable current input and move focus to previous if not the first one
                if (index !== 0) {
                    if (inputRefs.current[index]) {
                        inputRefs.current[index]!.disabled = true;
                    }
                    // Move focus to previous input
                    inputRefs.current[index - 1]?.focus();
                }
            }
        }
    };

    // Handle paste event
    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text").trim();

        // Only allow numeric values
        const numericData = pastedData.replace(/[^0-9]/g, "");

        // Check if pasted data has at least 1 digit (up to 6)
        if (numericData.length > 0) {
            // Take only first 6 digits
            const digits = numericData.slice(0, 6).split("");

            // Clear all inputs first and disable all except first
            for (let i = 0; i < 6; i++) {
                otpRef.current[i] = "";
                if (inputRefs.current[i]) {
                    inputRefs.current[i]!.value = "";
                    inputRefs.current[i]!.disabled = i !== 0;
                }
            }

            // Update ref and DOM for pasted digits, enabling each input sequentially
            digits.forEach((digit, idx) => {
                otpRef.current[idx] = digit;
                if (inputRefs.current[idx]) {
                    inputRefs.current[idx]!.value = digit;
                    inputRefs.current[idx]!.disabled = false;

                    // Enable next input if not the last one
                    if (idx < 5 && inputRefs.current[idx + 1]) {
                        inputRefs.current[idx + 1]!.disabled = false;
                    }
                }
            });

            // Check if all 6 digits are filled
            const allFilled = otpRef.current.every((d) => d !== "");
            setIsOtpComplete(allFilled);

            // Focus the next empty input or last filled input
            const nextEmptyIndex = digits.length < 6 ? digits.length : 5;
            inputRefs.current[nextEmptyIndex]?.focus();
        }
    };

    // Verify OTP API call
    const handleVerifyOtp = async () => {
        setIsLoading(true);
        setError("");

        try {
            const endpoint = import.meta.env.VITE_VERIFY_OTP;
            const otpString = otpRef.current.join("");

            const response = await api.post(endpoint, {
                email,
                otp: otpString,
            });

            if (response.status === 200) {
                const { message_id, data, tokens } = response.data;

                if (message_id === "2001" || message_id === 2001) {
                    // Store user data and tokens
                    if (data && tokens) {
                        login({
                            userId: data.user_id,
                            role: data.role,
                            email: data.email || email,
                            firstName: data.first_name,
                            lastName: data.last_name,
                            accessToken: tokens.access_token,
                            refreshToken: tokens.refresh_token,
                            firebaseToken: tokens.firebase_token || "",
              // Token expiration - should come from API response
              accessTokenExp: tokens.access_token_exp || null,
              refreshTokenExp: tokens.refresh_token_exp || null,
            });
            
            // console.log('🔑 Login successful with tokens:', {
            //   hasAccessToken: !!tokens.access_token,
            //   hasRefreshToken: !!tokens.refresh_token,
            //   hasFirebaseToken: !!tokens.firebase_token,
            //   accessTokenExp: tokens.access_token_exp || 'Not provided',
            //   refreshTokenExp: tokens.refresh_token_exp || 'Not provided',
            // });

            // console.log("tokens: ", tokens);
            if (tokens.firebase_token) {
              await signInWithFirebaseCustomToken(tokens.firebase_token);
            }
            
                        // Navigate to home or dashboard
                        navigate("/");
                    }
                }
            }
        } catch (err: any) {
            if (err.response?.data?.error_id === 4014) {
                setError(t("otp_code_incorrect_error"));
            } else if (err.response?.data?.error_id === 4291) {
                setError(t("otp_maximum_limit_error"));
            } else if (err.response?.data?.error_id === 40413) {
                setError(
                    t("user_not_exist")
                );
            } else {
                setError(t("something_went_wrong_error"));
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Resend OTP API call
    const handleResendOtp = async () => {
        setIsResending(true);
        setError("");

        try {
            const endpoint = import.meta.env.VITE_SEND_OTP;

            const response = await api.post(endpoint, {
                email,
                language: "en",
            });

            if (response.status === 200 || response.status === 201) {
                // Reset timers using refs
                durationRef.current = response.data.duration || initialDuration;
                resendingDurationRef.current =
                    response.data.resending_duration || initialResendingDuration;

                // Restart duration timer (visible timer)
                if (durationIntervalRef.current) {
                    clearInterval(durationIntervalRef.current);
                }

                const newDurationEndTime = Date.now() + durationRef.current * 1000;
                durationIntervalRef.current = setInterval(() => {
                    const remaining = Math.max(0, Math.round((newDurationEndTime - Date.now()) / 1000));
                    durationRef.current = remaining;

                    if (durationTimerDisplayRef.current) {
                        durationTimerDisplayRef.current.textContent = formatTime(remaining);
                    }

                    if (remaining <= 0 && durationIntervalRef.current) {
                        clearInterval(durationIntervalRef.current);
                    }
                }, 500);

                // Restart resending duration timer (hidden timer for resend button)
                setCanResend(false);
                if (resendIntervalRef.current) {
                    clearInterval(resendIntervalRef.current);
                }

                const newResendEndTime = Date.now() + resendingDurationRef.current * 1000;
                resendIntervalRef.current = setInterval(() => {
                    const remaining = Math.max(0, Math.round((newResendEndTime - Date.now()) / 1000));
                    resendingDurationRef.current = remaining;

                    if (remaining <= 0 && resendIntervalRef.current) {
                        clearInterval(resendIntervalRef.current);
                        setCanResend(true);
                    }
                }, 500);

                // Clear OTP inputs and reset disabled states
                otpRef.current = ["", "", "", "", "", ""];
                setIsOtpComplete(false);
                inputRefs.current.forEach((input, idx) => {
                    if (input) {
                        input.value = "";
                        input.disabled = idx !== 0;
                    }
                });
                inputRefs.current[0]?.focus();
            }
        } catch (err: any) {
            if (err.response?.data?.error_id === 4003) {
                setError(t("this_email_doesnot_exist_error_message"));
            } else if (err.response?.data?.error_id === 4292) {
                setError(t("otp_limit_exceeded_error"));
            } else {
                setError(t("something_went_wrong_error"));
            }
        } finally {
            setIsResending(false);
        }
    };

    return (
        <div className="auth-container">
            <AuthPageHeader title={title} details={details} />
            <div className="auth-page">
                {/* Error message */}
                {error && <div className="error-message">{error}</div>}

                {/* OTP Input Fields */}
                <div className="form-wrapper verification-form">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                        <input
                            key={index}
                            ref={setInputRef(index)}
                            className="input verify-input"
                            type="tel"
                            maxLength={1}
                            defaultValue=""
                            onChange={(e) => handleChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            onPaste={index === 0 ? handlePaste : undefined}
                            pattern="[0-9]"
                            inputMode="numeric"
                            autoFocus={index === 0}
                            disabled={index !== 0}
                        />
                    ))}
                </div>

                {/* Actions */}
                <div className="actions">
                    <div className="left-side">
                        {canResend && (
                            <button
                                className="btn btn-resend"
                                onClick={handleResendOtp}
                                disabled={isResending}
                            >
                                {t("resend_code")}
                            </button>
                        )}
                        <p className="timer" ref={durationTimerDisplayRef}>
                            {formatTime(durationRef.current)}
                        </p>
                    </div>

                    <button
                        className={`btn btn-primary${!isOtpComplete || isLoading ? " disabled" : ""
                            }`}
                        onClick={handleVerifyOtp}
                        disabled={!isOtpComplete || isLoading}
                    >
                        {t("continue_button")}
                    </button>
                </div>
            </div>
        </div>
    );
};
